import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AdminAPI } from '@shared/types/api';

// No-show fee hooks (/api/admin/ops/no-shows). The peer-report stats that feed
// detection live in useAdminNoShows.ts.

export function useNoShows(filters: AdminAPI.GetNoShowsRequest = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
  const qs = params.toString();
  return useQuery({
    // Prefix-keyed so the mutations' invalidate hits every filter combination.
    queryKey: ['admin-no-shows', qs],
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<AdminAPI.GetNoShowsResponse> => api.get(`/admin/ops/no-shows${qs ? `?${qs}` : ''}`),
  });
}

function useInvalidateNoShows() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['admin-no-shows'] });
}

export function useNoShowBatchAction() {
  const invalidate = useInvalidateNoShows();
  return useMutation({
    mutationFn: async (body: AdminAPI.NoShowBatchActionRequest): Promise<AdminAPI.NoShowBatchActionResponse> =>
      api.post('/admin/ops/no-shows/cases/batch', body),
    onSuccess: invalidate,
  });
}

export function useRunNoShowReconciler() {
  const invalidate = useInvalidateNoShows();
  return useMutation({
    mutationFn: async (dryRun: boolean): Promise<AdminAPI.RunNoShowReconcilerResponse> =>
      api.post('/admin/ops/no-shows/run', { dryRun }),
    onSuccess: invalidate,
  });
}

/** Flag a booking as a no-show by hand (matching page, user detail). */
export function useOpenNoShowCase() {
  const invalidate = useInvalidateNoShows();
  return useMutation({
    mutationFn: async (bookingId: string): Promise<AdminAPI.NoShowCaseActionResponse> =>
      api.post('/admin/ops/no-shows/cases', { bookingId }),
    onSuccess: invalidate,
  });
}
